import * as Sentry from "@sentry/react";
import axios from "axios";
import { cp } from "../config";
import { AUSTRIA, FEDERAL_STATES, type FederalStateCode } from "../constants/FederalStates";
import { getValidIsbn13s } from "../helpers/Isbn";

/** One volume of the book catalog: an article of the shop search, with the states it is approved in. */
export interface BookCatalogVolume {
    subjectId: string;
    subjectName: string;
    publisherId: string;
    publisherName: string;
    bookId: string;
    bookName: string;
    /** The id p6u/get-band-info/ resolves the volume by. */
    articleId: string;
    /** The volume on its own, e.g. "Access Bayern 5". */
    fullName: string;
    /** The volume within its book, e.g. "Band 1", "Gesamtband", "Klasse 5". */
    name: string;
    imageUrl: string;
    isbns: Array<string>;
    /** e.g. ["8. Klasse", "9. Klasse"]; empty when unknown. */
    schoolGrades: Array<string>;
    /** Codes of the federal states the volume is approved in, AUSTRIA for Austrian-only volumes; empty when none is on record. */
    federalStates: Array<string>;
    isComingSoon: boolean;
}

/** The shop search runs on the main website of each environment. */
const getShopSearchUrl = () => `${String(cp.cfg.REACT_APP_P6_BASE_WEBSITE).replace(/\/$/, "")}/shop-search-server`;
const SHOP_SEARCH_HEADERS = { Accept: "application/json;version=1.0" };
const SHOP_SEARCH_TIMEOUT_MS = 15000;
const ARTICLE_PARAMS = {
    fields: "id,name,nameFull,imageUrl,isbns,schoolGrade,comingSoon",
    include: "book,subject,publisher",
    "fields[book]": "id,name",
    "fields[subject]": "id,name",
    "fields[publisher]": "id,name",
};

interface ShopArticle {
    id: number;
    name: string;
    nameFull: string;
    imageUrl?: string;
    isbns?: Array<string>;
    schoolGrade?: Array<string>;
    comingSoon?: boolean;
    book?: { id: number; name: string };
    subject?: { id: string; name: string };
    publisher?: { id: string; name: string };
}

/** Without a page limit the shop search returns every match at once. */
const getShopArticles = async <T>(shop: "DE" | "AT", params: Record<string, string>): Promise<Array<T>> => {
    const response = await axios.get(`${getShopSearchUrl()}/shops/${shop}/articles`, {
        params,
        headers: SHOP_SEARCH_HEADERS,
        timeout: SHOP_SEARCH_TIMEOUT_MS,
    });
    return response.data.data;
};

const getArticleIdsByState = (): Promise<Array<readonly [FederalStateCode, Set<number>]>> =>
    Promise.all(
        FEDERAL_STATES.map(async (state) => {
            const articles = await getShopArticles<{ id: number }>("DE", {
                fields: "id",
                "filter[countryRegionId]": state.regionId,
            });
            return [state.code, new Set(articles.map((article) => article.id))] as const;
        })
    );

const toCatalogVolume = (article: ShopArticle, states: Array<string>): BookCatalogVolume | undefined => {
    if (!article.book || !article.subject || !article.publisher) {
        return undefined;
    }

    return {
        subjectId: article.subject.id,
        subjectName: article.subject.name,
        publisherId: article.publisher.id,
        publisherName: article.publisher.name,
        bookId: String(article.book.id),
        bookName: article.book.name.trim(),
        articleId: String(article.id),
        fullName: article.nameFull,
        name: article.name,
        imageUrl: article.imageUrl ?? "",
        isbns: getValidIsbn13s(article.isbns ?? []),
        schoolGrades: article.schoolGrade ?? [],
        federalStates: states,
        isComingSoon: !!article.comingSoon,
    };
};

/**
 * The whole catalog, so the selection can filter and count on the client. The shop search only filters by
 * state and never lists an article's states, so they come from one id-only request per state.
 * Only the German articles are required; without the rest the selection still works, just less precisely.
 */
export interface BookCatalog {
    volumes: Array<BookCatalogVolume>;
    /** False when the federal states couldn't be loaded: every volume then counts as approved everywhere. */
    hasFederalStates: boolean;
}

export const loadBookCatalog = async (): Promise<BookCatalog> => {
    const [germanArticles, austrianArticles, articleIdsByState] = await Promise.all([
        getShopArticles<ShopArticle>("DE", ARTICLE_PARAMS),
        getShopArticles<ShopArticle>("AT", ARTICLE_PARAMS).catch((error) => {
            Sentry.captureException(error, { extra: { context: "book catalog: Austrian articles" } });
            return [];
        }),
        // All or none: with only some states known, volumes of the missing ones would be hidden there.
        getArticleIdsByState().catch((error) => {
            Sentry.captureException(error, { extra: { context: "book catalog: federal states" } });
            return undefined;
        }),
    ]);

    const statesOf = (articleId: number) =>
        (articleIdsByState ?? []).filter(([, articleIds]) => articleIds.has(articleId)).map(([code]) => code);
    const germanArticleIds = new Set(germanArticles.map((article) => article.id));

    const volumes = [
        ...germanArticles.map((article) => toCatalogVolume(article, statesOf(article.id))),
        ...austrianArticles
            .filter((article) => !germanArticleIds.has(article.id))
            .map((article) => toCatalogVolume(article, [AUSTRIA])),
    ].filter((volume): volume is BookCatalogVolume => !!volume);

    return { volumes, hasFederalStates: !!articleIdsByState };
};

let catalogRequest: Promise<BookCatalog> | undefined;

/** Loaded once per page load, as the catalog barely changes; a failed load is retried on the next call. */
export const getBookCatalog = (): Promise<BookCatalog> => {
    catalogRequest ??= loadBookCatalog().catch((error) => {
        catalogRequest = undefined;
        Sentry.captureException(error, { extra: { context: "book catalog" } });
        throw error;
    });
    return catalogRequest;
};
